import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import BASE_URL from '../../config/config';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import DOMPurify from 'dompurify';
import he from 'he';
// Función para decodificar entidades HTML
const decodeHtml = (html) => {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

const Proyecto = () => {
  // Mapeo de tipos
  const typeMapping = {
    rrss: 'Redes Sociales',
    igualadiseo: 'Creativo',
    bug: 'Iguala SEM',
    task: 'Desarrollo',
    story: 'Sin especificar',
    epic: 'Marketing',
    igualamarketing: 'Iguala SEO',
    administracin: 'Administración'
  };

  const { id } = useParams();

  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [yearFilter, setYearFilter] = useState('2025');
  const [nameFilter, setNameFilter] = useState('');
  const activeProjectRef = useRef(null);

  // Estado para la tarea seleccionada (para mostrar sus comentarios)
  const [selectedTask, setSelectedTask] = useState(null);

  // Obtener tareas del proyecto
  useEffect(() => {
    setLoadingTasks(true);
    setTasks([]);

    const fetchTasks = async () => {
      if (id) {
        try {
          const response = await axios.get(`${BASE_URL}/api/proyecto/${id}/tasks`);
          if (response.data) {
            setTasks(response.data.tasks);
          } else {
            setTasks([]);
          }
        } catch (error) {
          console.error('Error al obtener las tareas:', error.message);
        } finally {
          setLoadingTasks(false);
        }
      } else {
        setLoadingTasks(false);
      }
    };

    fetchTasks();
  }, [id]);

  // Obtener lista de proyectos
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/cachedFiles/todosLosProyectos.txt`);
        setProjects(response.data);
      } catch (error) {
        console.error('Error al cargar proyectos:', error.message);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  // Función para filtrar proyectos por año
  const filterByYear = (project, year) => {
    if (year === 'all') return true;
    const startOfYear = new Date(year, 0, 1).getTime() / 1000;
    const endOfYear = new Date(year, 11, 31, 23, 59, 59).getTime() / 1000;
    return project.date >= startOfYear && project.dueDate <= endOfYear;
  };

  // Filtrar proyectos
  const filteredProjects = projects.filter(project => 
    filterByYear(project, yearFilter) &&
    (nameFilter.trim() === '' || project.name.toLowerCase().includes(nameFilter.toLowerCase()))
  );

  // Asegurarse de que el proyecto activo esté visible
  useEffect(() => {
    if (activeProjectRef.current) {
      activeProjectRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [id, filteredProjects]);

  // Definición de columnas para las tareas
  const columns = [
    { key: 'new', label: 'No aún' },
    { key: 'duplicate', label: 'Por empezar' },
    { key: 'in_progress', label: 'En progreso' },
    { key: 'cliente', label: 'Cliente' },
    { key: 'done', label: 'Hecho' },
    { key: 'verified', label: 'Facturar' },
    { key: 'facturar', label: 'Facturada' },
  ];

  return (
    <main className="bg-gris">
      <div className="site-width proyecto-grid">
        {/* Panel izquierdo: listado de proyectos */}
        <div className="left-panel">
          <h2>Proyectos</h2>
          <div className="filtros-proyecto" style={{ marginBottom: '1rem' }}>
            <input
              id="nameFilter"
              type="text"
              placeholder="Filtrar por nombre..."
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
            <select
              id="yearFilter"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
            >
              {[2025, 2024, 2023, 2022, 2021, 2020].map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
              <option value="all">Todos</option>
            </select>
          </div>

          <div
            className="grid-proyectos-individual"
            style={{ maxHeight: '500px', overflowY: 'auto' }}
          >
            {loadingProjects ? (
              <p>Cargando proyectos...</p>
            ) : filteredProjects.length === 0 ? (
              <p className='no-proyectos-disp'>Proyecto no encontrado.</p>
            ) : (
              filteredProjects.map((project) => (
                <Link
                  to={`/proyecto/${project.id}`}
                  className="proyecto-item"
                  style={{
                    backgroundColor: project.id === id ? '#e0e0e0' : '#f7f7f7',
                  }}
                  ref={project.id === id ? activeProjectRef : undefined}
                  key={project.id}
                >
                  <h4>{project.name}</h4>
                  <div className="proyecto-item-fecha">
                    <p className="fecha-inicio">
                      Inicio: {new Date(project.date * 1000).toLocaleDateString('es-ES')}
                    </p>
                    <p className="fecha-fin">
                      Fin: {new Date(project.dueDate * 1000).toLocaleDateString('es-ES')}
                    </p>
                  </div>
                  <div className="proyecto-item-tags">
                    {project.tags && project.tags.length > 0 ? (
                      project.tags.map((tag, i) => (
                        <span key={i} className="tag">
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="tag tag-empty">Sin etiquetas</span>
                    )}
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Panel derecho: Tareas del proyecto */}
        <div className="right-panel">
          <h2>Tareas del Proyecto</h2>
          {loadingTasks ? (
            <div className="overflow-tareas">
              <div className="grid-tareas">
                <Skeleton count={3} height={180} style={{ marginBottom: '1rem' }} />
                <Skeleton count={3} height={180} style={{ marginBottom: '1rem' }} />
                <Skeleton count={3} height={180} style={{ marginBottom: '1rem' }} />
              </div>
            </div>
          ) : tasks.length === 0 ? (
            <p className='no-tareas-disp'>¡Oops! Parece que todavía no se han añadido tareas a este proyecto.</p>
          ) : (
            <div className="overflow-tareas">
              <div className="grid-tareas">
                {columns.map((column) => {
                  // Filtrar las tareas según el status (columna)
                  const tasksForColumn = tasks.filter(
                    (task) => task?.data?.status === column.key
                  );
                  
                  return (
                    <div key={column.key} className={`tarea-columna ${column.key}`}>
                      <h4>{column.label}</h4>
                      <div className='grid-mini-tareas'>
                        {tasksForColumn.length === 0 ? (
                          <p className='no-tareas'>No hay tareas</p>
                        ) : (
                          tasksForColumn.map((task, index) => (
                            <div
                              key={index}
                              className="tarea-item"
                              onClick={() => setSelectedTask(task)}
                              style={{ cursor: 'pointer' }}
                            >
                              <h5>{he.decode(task.data.name)}</h5>
                              <p className="item-dpto">
                                {typeMapping[task.data.type] || task.data.type}
                              </p>
                              <div className="tarea-timestamps">
                                <p>
                                  Creado:{' '}
                                  {new Date(task.data.createdAt * 1000).toLocaleDateString('es-ES', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit'
                                  })}
                                </p>
                                <p>
                                  Última actualización:{' '}
                                  {new Date(task.data.updatedAt * 1000).toLocaleDateString('es-ES', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal para mostrar los comentarios de la tarea seleccionada */}
      {selectedTask && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <button
              onClick={() => setSelectedTask(null)}
              aria-label="Cerrar"
            >
              &times;
            </button>
            <h2>Comentarios de la tarea: {he.decode(selectedTask.data.name)}</h2>
            <p>Holded ofrece una versión reducida de comentarios, para más detalles visita <a href={`https://app.holded.com/projects/p/${id}/basic`} target='_blank' rel='noreferer'>el proyecto</a>.</p>
            <div className="comments">
            {selectedTask.data.comments && selectedTask.data.comments.length > 0 ? (
              selectedTask.data.comments.map((comment) => (
                <div key={comment.commentId} className="comment">
                  {/* Se decodifican las entidades HTML y luego se eliminan todas las etiquetas */}
                  <p>
                  {he.decode(DOMPurify.sanitize(decodeHtml(comment.message), { ALLOWED_TAGS: [] }))}
                  </p>
                  <span>
                    {new Date(comment.createdAt * 1000).toLocaleString('es-ES')}
                  </span>
                </div>
              ))
            ) : (
              <p className='no-comentarios-disp'>No se añadieron comentarios en esta tarea.</p>
            )}
              </div>
          </div>
        </div>
      )}
    </main>
  );
};


export default Proyecto;
